import { StateCreator } from 'zustand';
import { WorkspaceInterfaceDetailed } from '~/interfaces/entities';
import { WORKSPACE_STORAGE_KEY } from '~/modules/Workspaces/constants/workspaceStorage';
import getDefaultWsToSelect from '~/modules/Workspaces/helpers/getDefaultWsToSelect';
import { WorkspacesStoreInterface } from '~/modules/Workspaces/types/workspacesStore.interface';
import { workspaceServices } from '~/services';
import { GlobalStoreInterface } from '~/store/types/globalStore.interface';
import { QueryParamsInterface } from '~/types/queryParams.interface';
import { getQueryParams } from '~/utils/getQueryParams';
import { setItemInLocalStorage } from '~/utils/handleItemInLocalStorage';

const queryParams = getQueryParams<QueryParamsInterface>('params');

/*
    Based in our business logic, fetching data may implies some other cascade actions.
    You must specify in each method the consecuent actions that must be executed.
*/

const createWorkspacesSlice: StateCreator<
    GlobalStoreInterface,
    [],
    [],
    WorkspacesStoreInterface
> = (
    set: (state: Partial<WorkspacesStoreInterface>) => void,
    get: () => GlobalStoreInterface
) => ({
    selectedWorkspaceId: null,
    workspaces: [],
    loadingWs: false,
    loadingWsMembers: false,
    wsMembers: [],
    wsInvitations: [],

    handleWorkspaces: (workspaces: WorkspaceInterfaceDetailed[]) =>
        set({ workspaces }),
    handleSelectedWorkspace: (wsId: string) => {
        // store the latest workspace in Lstorage
        setItemInLocalStorage(WORKSPACE_STORAGE_KEY, wsId);

        set({ selectedWorkspaceId: wsId });
    },
    fetchWorkspaces: onFetchWorkspaces(set, get),
    fetchMembersAndInvitations: onFetchWorkspaceMembersAndInvitations(set, get),
});

const onFetchWorkspaces: (
    set: (state: Partial<WorkspacesStoreInterface>) => void,
    get: () => GlobalStoreInterface
) => () => Promise<void> = (set, get) => async () => {
    set({ loadingWs: true });

    const { error, data } = await workspaceServices.getWorkspaces({
        all: true,
    });

    set({ loadingWs: false });

    if (error) return;

    set({ workspaces: data });

    const defaultWs = getDefaultWsToSelect(queryParams, data);

    get().handleSelectedWorkspace(defaultWs);

    // SIDE EFFECTS:
    get().fetchMembersAndInvitations();
};

const onFetchWorkspaceMembersAndInvitations: (
    set: (state: Partial<WorkspacesStoreInterface>) => void,
    get: () => GlobalStoreInterface
) => () => Promise<void> = (set, get) => async () => {
    set({ loadingWsMembers: true });

    const wsId = get().selectedWorkspaceId;

    const { error, members, invitations } =
        await workspaceServices.getMembersAndInvitations(wsId as string);

    set({ loadingWsMembers: false });

    if (error) return;

    set({ wsMembers: members, wsInvitations: invitations });
};

export default createWorkspacesSlice;
